import {
  createUseReadContract,
  createUseWriteContract,
  createUseSimulateContract,
  createUseWatchContractEvent,
} from 'wagmi/codegen'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ERC20
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const erc20Abi = [
  {
    type: 'event',
    inputs: [
      { name: 'owner', type: 'address', indexed: true },
      { name: 'spender', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Approval',
  },
  {
    type: 'event',
    inputs: [
      { name: 'from', type: 'address', indexed: true },
      { name: 'to', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Transfer',
  },
  {
    type: 'function',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'decimals',
    outputs: [{ type: 'uint8' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'name',
    outputs: [{ type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'symbol',
    outputs: [{ type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'totalSupply',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'sender', type: 'address' },
      { name: 'recipient', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'transferFrom',
    outputs: [{ type: 'bool' }],
    stateMutability: 'nonpayable',
  },
] as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ERC20Vault
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const erc20VaultAbi = [
  { type: 'error', inputs: [], name: 'ETH_TRANSFER_FAILED' },
  { type: 'error', inputs: [], name: 'FUNC_NOT_IMPLEMENTED' },
  { type: 'error', inputs: [], name: 'INVALID_PAUSE_STATUS' },
  { type: 'error', inputs: [], name: 'REENTRANT_CALL' },
  { type: 'error', inputs: [], name: 'RESOLVER_DENIED' },
  { type: 'error', inputs: [], name: 'RESOLVER_INVALID_MANAGER' },
  { type: 'error', inputs: [], name: 'RESOLVER_UNEXPECTED_CHAINID' },
  {
    type: 'error',
    inputs: [
      { name: 'chainId', internalType: 'uint64', type: 'uint64' },
      { name: 'name', internalType: 'bytes32', type: 'bytes32' },
    ],
    name: 'RESOLVER_ZERO_ADDR',
  },
  { type: 'error', inputs: [], name: 'VAULT_BTOKEN_BLACKLISTED' },
  { type: 'error', inputs: [], name: 'VAULT_CTOKEN_MISMATCH' },
  { type: 'error', inputs: [], name: 'VAULT_INSUFFICIENT_FEE' },
  { type: 'error', inputs: [], name: 'VAULT_INVALID_AMOUNT' },
  { type: 'error', inputs: [], name: 'VAULT_INVALID_CTOKEN' },
  { type: 'error', inputs: [], name: 'VAULT_INVALID_NEW_BTOKEN' },
  { type: 'error', inputs: [], name: 'VAULT_INVALID_TOKEN' },
  { type: 'error', inputs: [], name: 'VAULT_INVALID_TO_ADDR' },
  { type: 'error', inputs: [], name: 'VAULT_LAST_MIGRATION_TOO_CLOSE' },
  { type: 'error', inputs: [], name: 'VAULT_PERMISSION_DENIED' },
  { type: 'error', inputs: [], name: 'ZERO_ADDRESS' },
  { type: 'error', inputs: [], name: 'ZERO_VALUE' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'previousAdmin',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'newAdmin',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
    ],
    name: 'AdminChanged',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'beacon',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'BeaconUpgraded',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'srcChainId',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'ctoken',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'btokenOld',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'btokenNew',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'ctokenSymbol',
        internalType: 'string',
        type: 'string',
        indexed: false,
      },
      {
        name: 'ctokenName',
        internalType: 'string',
        type: 'string',
        indexed: false,
      },
      {
        name: 'ctokenDecimal',
        internalType: 'uint8',
        type: 'uint8',
        indexed: false,
      },
    ],
    name: 'BridgedTokenChanged',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'srcChainId',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'ctoken',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'btoken',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'ctokenSymbol',
        internalType: 'string',
        type: 'string',
        indexed: false,
      },
      {
        name: 'ctokenName',
        internalType: 'string',
        type: 'string',
        indexed: false,
      },
      {
        name: 'ctokenDecimal',
        internalType: 'uint8',
        type: 'uint8',
        indexed: false,
      },
    ],
    name: 'BridgedTokenDeployed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'version', internalType: 'uint8', type: 'uint8', indexed: false },
    ],
    name: 'Initialized',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'previousOwner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'newOwner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'OwnershipTransferStarted',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'previousOwner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'newOwner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'OwnershipTransferred',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'account',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
    ],
    name: 'Paused',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'msgHash',
        internalType: 'bytes32',
        type: 'bytes32',
        indexed: true,
      },
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      { name: 'to', internalType: 'address', type: 'address', indexed: true },
      {
        name: 'srcChainId',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
      {
        name: 'ctoken',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'TokenReceived',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'msgHash',
        internalType: 'bytes32',
        type: 'bytes32',
        indexed: true,
      },
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      {
        name: 'ctoken',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'TokenReleased',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'msgHash',
        internalType: 'bytes32',
        type: 'bytes32',
        indexed: true,
      },
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      { name: 'to', internalType: 'address', type: 'address', indexed: true },
      {
        name: 'canonicalChainId',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
      {
        name: 'destChainId',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
      {
        name: 'ctoken',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'TokenSent',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'account',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
    ],
    name: 'Unpaused',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'implementation',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'Upgraded',
  },
  {
    type: 'function',
    inputs: [],
    name: 'MIN_MIGRATION_DELAY',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'acceptOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'addressManager',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'btoken', internalType: 'address', type: 'address' }],
    name: 'bridgedToCanonical',
    outputs: [
      { name: 'chainId', internalType: 'uint64', type: 'uint64' },
      { name: 'addr', internalType: 'address', type: 'address' },
      { name: 'decimals', internalType: 'uint8', type: 'uint8' },
      { name: 'symbol', internalType: 'string', type: 'string' },
      { name: 'name', internalType: 'string', type: 'string' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'btoken', internalType: 'address', type: 'address' }],
    name: 'btokenDenylist',
    outputs: [{ name: 'denied', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'chainId', internalType: 'uint256', type: 'uint256' },
      { name: 'ctoken', internalType: 'address', type: 'address' },
    ],
    name: 'canonicalToBridged',
    outputs: [{ name: 'btoken', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      {
        name: '_ctoken',
        internalType: 'struct ERC20Vault.CanonicalERC20',
        type: 'tuple',
        components: [
          { name: 'chainId', internalType: 'uint64', type: 'uint64' },
          { name: 'addr', internalType: 'address', type: 'address' },
          { name: 'decimals', internalType: 'uint8', type: 'uint8' },
          { name: 'symbol', internalType: 'string', type: 'string' },
          { name: 'name', internalType: 'string', type: 'string' },
        ],
      },
      { name: '_btokenNew', internalType: 'address', type: 'address' },
    ],
    name: 'changeBridgedToken',
    outputs: [{ name: 'btokenOld_', internalType: 'address', type: 'address' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'impl',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'inNonReentrant',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: '_owner', internalType: 'address', type: 'address' },
      { name: '_addressManager', internalType: 'address', type: 'address' },
    ],
    name: 'init',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'chainId', internalType: 'uint256', type: 'uint256' },
      { name: 'ctoken', internalType: 'address', type: 'address' },
    ],
    name: 'lastMigrationStart',
    outputs: [{ name: 'timestamp', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'lastUnpausedAt',
    outputs: [{ name: '', internalType: 'uint64', type: 'uint64' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'name',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'pure',
  },
  {
    type: 'function',
    inputs: [{ name: '_data', internalType: 'bytes', type: 'bytes' }],
    name: 'onMessageInvocation',
    outputs: [],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: '_message',
        internalType: 'struct IBridge.Message',
        type: 'tuple',
        components: [
          { name: 'id', internalType: 'uint64', type: 'uint64' },
          { name: 'fee', internalType: 'uint64', type: 'uint64' },
          { name: 'gasLimit', internalType: 'uint32', type: 'uint32' },
          { name: 'from', internalType: 'address', type: 'address' },
          { name: 'srcChainId', internalType: 'uint64', type: 'uint64' },
          { name: 'srcOwner', internalType: 'address', type: 'address' },
          { name: 'destChainId', internalType: 'uint64', type: 'uint64' },
          { name: 'destOwner', internalType: 'address', type: 'address' },
          { name: 'to', internalType: 'address', type: 'address' },
          { name: 'value', internalType: 'uint256', type: 'uint256' },
          { name: 'data', internalType: 'bytes', type: 'bytes' },
        ],
      },
      { name: '_msgHash', internalType: 'bytes32', type: 'bytes32' },
    ],
    name: 'onMessageRecalled',
    outputs: [],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'owner',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'pause',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'paused',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'pendingOwner',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'proxiableUUID',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'renounceOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: '_chainId', internalType: 'uint64', type: 'uint64' },
      { name: '_name', internalType: 'bytes32', type: 'bytes32' },
      { name: '_allowZeroAddress', internalType: 'bool', type: 'bool' },
    ],
    name: 'resolve',
    outputs: [{ name: '', internalType: 'address payable', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: '_name', internalType: 'bytes32', type: 'bytes32' },
      { name: '_allowZeroAddress', internalType: 'bool', type: 'bool' },
    ],
    name: 'resolve',
    outputs: [{ name: '', internalType: 'address payable', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      {
        name: '_op',
        internalType: 'struct ERC20Vault.BridgeTransferOp',
        type: 'tuple',
        components: [
          { name: 'destChainId', internalType: 'uint64', type: 'uint64' },
          { name: 'destOwner', internalType: 'address', type: 'address' },
          { name: 'to', internalType: 'address', type: 'address' },
          { name: 'fee', internalType: 'uint64', type: 'uint64' },
          { name: 'token', internalType: 'address', type: 'address' },
          { name: 'gasLimit', internalType: 'uint32', type: 'uint32' },
          { name: 'amount', internalType: 'uint256', type: 'uint256' },
        ],
      },
    ],
    name: 'sendToken',
    outputs: [
      {
        name: 'message_',
        internalType: 'struct IBridge.Message',
        type: 'tuple',
        components: [
          { name: 'id', internalType: 'uint64', type: 'uint64' },
          { name: 'fee', internalType: 'uint64', type: 'uint64' },
          { name: 'gasLimit', internalType: 'uint32', type: 'uint32' },
          { name: 'from', internalType: 'address', type: 'address' },
          { name: 'srcChainId', internalType: 'uint64', type: 'uint64' },
          { name: 'srcOwner', internalType: 'address', type: 'address' },
          { name: 'destChainId', internalType: 'uint64', type: 'uint64' },
          { name: 'destOwner', internalType: 'address', type: 'address' },
          { name: 'to', internalType: 'address', type: 'address' },
          { name: 'value', internalType: 'uint256', type: 'uint256' },
          { name: 'data', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [{ name: '_interfaceId', internalType: 'bytes4', type: 'bytes4' }],
    name: 'supportsInterface',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'newOwner', internalType: 'address', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'unpause',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'newImplementation', internalType: 'address', type: 'address' },
    ],
    name: 'upgradeTo',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'newImplementation', internalType: 'address', type: 'address' },
      { name: 'data', internalType: 'bytes', type: 'bytes' },
    ],
    name: 'upgradeToAndCall',
    outputs: [],
    stateMutability: 'payable',
  },
] as const

/**
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const erc20VaultAddress = {
  17000: '0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa',
  167009: '0x1670090000000000000000000000000000000002',
} as const

/**
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const erc20VaultConfig = {
  address: erc20VaultAddress,
  abi: erc20VaultAbi,
} as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// EnsRegistry
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const ensRegistryAbi = [
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [{ name: 'node', type: 'bytes32' }],
    name: 'resolver',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [{ name: 'node', type: 'bytes32' }],
    name: 'owner',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: 'node', type: 'bytes32' },
      { name: 'label', type: 'bytes32' },
      { name: 'owner', type: 'address' },
    ],
    name: 'setSubnodeOwner',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: 'node', type: 'bytes32' },
      { name: 'ttl', type: 'uint64' },
    ],
    name: 'setTTL',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [{ name: 'node', type: 'bytes32' }],
    name: 'ttl',
    outputs: [{ name: '', type: 'uint64' }],
    stateMutability: 'view',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: 'node', type: 'bytes32' },
      { name: 'resolver', type: 'address' },
    ],
    name: 'setResolver',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: 'node', type: 'bytes32' },
      { name: 'owner', type: 'address' },
    ],
    name: 'setOwner',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'node', type: 'bytes32', indexed: true },
      { name: 'owner', type: 'address', indexed: false },
    ],
    name: 'Transfer',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'node', type: 'bytes32', indexed: true },
      { name: 'label', type: 'bytes32', indexed: true },
      { name: 'owner', type: 'address', indexed: false },
    ],
    name: 'NewOwner',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'node', type: 'bytes32', indexed: true },
      { name: 'resolver', type: 'address', indexed: false },
    ],
    name: 'NewResolver',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'node', type: 'bytes32', indexed: true },
      { name: 'ttl', type: 'uint64', indexed: false },
    ],
    name: 'NewTTL',
  },
] as const

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const ensRegistryAddress = {
  1: '0x314159265dD8dbb310642f98f50C066173C1259b',
  11155111: '0x112234455C3a32FD11230C42E7Bccd4A84e02010',
} as const

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const ensRegistryConfig = {
  address: ensRegistryAddress,
  abi: ensRegistryAbi,
} as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Multicall3
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const multicall3Abi = [
  {
    type: 'function',
    inputs: [
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'aggregate',
    outputs: [
      { name: 'blockNumber', internalType: 'uint256', type: 'uint256' },
      { name: 'returnData', internalType: 'bytes[]', type: 'bytes[]' },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call3[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'allowFailure', internalType: 'bool', type: 'bool' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'aggregate3',
    outputs: [
      {
        name: 'returnData',
        internalType: 'struct Multicall3.Result[]',
        type: 'tuple[]',
        components: [
          { name: 'success', internalType: 'bool', type: 'bool' },
          { name: 'returnData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call3Value[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'allowFailure', internalType: 'bool', type: 'bool' },
          { name: 'value', internalType: 'uint256', type: 'uint256' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'aggregate3Value',
    outputs: [
      {
        name: 'returnData',
        internalType: 'struct Multicall3.Result[]',
        type: 'tuple[]',
        components: [
          { name: 'success', internalType: 'bool', type: 'bool' },
          { name: 'returnData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'blockAndAggregate',
    outputs: [
      { name: 'blockNumber', internalType: 'uint256', type: 'uint256' },
      { name: 'blockHash', internalType: 'bytes32', type: 'bytes32' },
      {
        name: 'returnData',
        internalType: 'struct Multicall3.Result[]',
        type: 'tuple[]',
        components: [
          { name: 'success', internalType: 'bool', type: 'bool' },
          { name: 'returnData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getBasefee',
    outputs: [{ name: 'basefee', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'blockNumber', internalType: 'uint256', type: 'uint256' }],
    name: 'getBlockHash',
    outputs: [{ name: 'blockHash', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getBlockNumber',
    outputs: [
      { name: 'blockNumber', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getChainId',
    outputs: [{ name: 'chainid', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getCurrentBlockCoinbase',
    outputs: [{ name: 'coinbase', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getCurrentBlockDifficulty',
    outputs: [{ name: 'difficulty', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getCurrentBlockGasLimit',
    outputs: [{ name: 'gaslimit', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getCurrentBlockTimestamp',
    outputs: [{ name: 'timestamp', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'addr', internalType: 'address', type: 'address' }],
    name: 'getEthBalance',
    outputs: [{ name: 'balance', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'getLastBlockHash',
    outputs: [{ name: 'blockHash', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'requireSuccess', internalType: 'bool', type: 'bool' },
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'tryAggregate',
    outputs: [
      {
        name: 'returnData',
        internalType: 'struct Multicall3.Result[]',
        type: 'tuple[]',
        components: [
          { name: 'success', internalType: 'bool', type: 'bool' },
          { name: 'returnData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'requireSuccess', internalType: 'bool', type: 'bool' },
      {
        name: 'calls',
        internalType: 'struct Multicall3.Call[]',
        type: 'tuple[]',
        components: [
          { name: 'target', internalType: 'address', type: 'address' },
          { name: 'callData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    name: 'tryBlockAndAggregate',
    outputs: [
      { name: 'blockNumber', internalType: 'uint256', type: 'uint256' },
      { name: 'blockHash', internalType: 'bytes32', type: 'bytes32' },
      {
        name: 'returnData',
        internalType: 'struct Multicall3.Result[]',
        type: 'tuple[]',
        components: [
          { name: 'success', internalType: 'bool', type: 'bool' },
          { name: 'returnData', internalType: 'bytes', type: 'bytes' },
        ],
      },
    ],
    stateMutability: 'payable',
  },
] as const

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const multicall3Address = {
  1: '0xcA11bde05977b3631167028862bE2a173976CA11',
  17000: '0xcA11bde05977b3631167028862bE2a173976CA11',
  167009: '0xcA11bde05977b3631167028862bE2a173976CA11',
} as const

/**
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const multicall3Config = {
  address: multicall3Address,
  abi: multicall3Abi,
} as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// RelendWrapped
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const relendWrappedAbi = [
  {
    type: 'constructor',
    inputs: [
      { name: '_asset', internalType: 'address', type: 'address' },
      { name: '_name', internalType: 'string', type: 'string' },
      { name: '_symbol', internalType: 'string', type: 'string' },
      { name: '_admin', internalType: 'address', type: 'address' },
    ],
    stateMutability: 'nonpayable',
  },
  { type: 'error', inputs: [], name: 'AccessControlBadConfirmation' },
  {
    type: 'error',
    inputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'neededRole', internalType: 'bytes32', type: 'bytes32' },
    ],
    name: 'AccessControlUnauthorizedAccount',
  },
  { type: 'error', inputs: [], name: 'ECDSAInvalidSignature' },
  {
    type: 'error',
    inputs: [{ name: 'length', internalType: 'uint256', type: 'uint256' }],
    name: 'ECDSAInvalidSignatureLength',
  },
  {
    type: 'error',
    inputs: [{ name: 's', internalType: 'bytes32', type: 'bytes32' }],
    name: 'ECDSAInvalidSignatureS',
  },
  {
    type: 'error',
    inputs: [
      { name: 'spender', internalType: 'address', type: 'address' },
      { name: 'allowance', internalType: 'uint256', type: 'uint256' },
      { name: 'needed', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'ERC20InsufficientAllowance',
  },
  {
    type: 'error',
    inputs: [
      { name: 'sender', internalType: 'address', type: 'address' },
      { name: 'balance', internalType: 'uint256', type: 'uint256' },
      { name: 'needed', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'ERC20InsufficientBalance',
  },
  {
    type: 'error',
    inputs: [{ name: 'approver', internalType: 'address', type: 'address' }],
    name: 'ERC20InvalidApprover',
  },
  {
    type: 'error',
    inputs: [{ name: 'receiver', internalType: 'address', type: 'address' }],
    name: 'ERC20InvalidReceiver',
  },
  {
    type: 'error',
    inputs: [{ name: 'sender', internalType: 'address', type: 'address' }],
    name: 'ERC20InvalidSender',
  },
  {
    type: 'error',
    inputs: [{ name: 'spender', internalType: 'address', type: 'address' }],
    name: 'ERC20InvalidSpender',
  },
  {
    type: 'error',
    inputs: [{ name: 'token', internalType: 'address', type: 'address' }],
    name: 'ERC20InvalidUnderlying',
  },
  {
    type: 'error',
    inputs: [{ name: 'deadline', internalType: 'uint256', type: 'uint256' }],
    name: 'ERC2612ExpiredSignature',
  },
  {
    type: 'error',
    inputs: [
      { name: 'signer', internalType: 'address', type: 'address' },
      { name: 'owner', internalType: 'address', type: 'address' },
    ],
    name: 'ERC2612InvalidSigner',
  },
  {
    type: 'error',
    inputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'currentNonce', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'InvalidAccountNonce',
  },
  { type: 'error', inputs: [], name: 'InvalidShortString' },
  {
    type: 'error',
    inputs: [{ name: 'token', internalType: 'address', type: 'address' }],
    name: 'SafeERC20FailedOperation',
  },
  {
    type: 'error',
    inputs: [{ name: 'str', internalType: 'string', type: 'string' }],
    name: 'StringTooLong',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'owner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'spender',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'value',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'Approval',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'burner',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'from',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'BurnFractionalReserve',
  },
  { type: 'event', anonymous: false, inputs: [], name: 'EIP712DomainChanged' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'minter',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      { name: 'to', internalType: 'address', type: 'address', indexed: false },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'MintFractionalReserve',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32', indexed: true },
      {
        name: 'previousAdminRole',
        internalType: 'bytes32',
        type: 'bytes32',
        indexed: true,
      },
      {
        name: 'newAdminRole',
        internalType: 'bytes32',
        type: 'bytes32',
        indexed: true,
      },
    ],
    name: 'RoleAdminChanged',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32', indexed: true },
      {
        name: 'account',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'sender',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'RoleGranted',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32', indexed: true },
      {
        name: 'account',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'sender',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'RoleRevoked',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'account',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'segregated',
        internalType: 'bool',
        type: 'bool',
        indexed: false,
      },
    ],
    name: 'SetSegregatedAccount',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'from', internalType: 'address', type: 'address', indexed: true },
      { name: 'to', internalType: 'address', type: 'address', indexed: true },
      {
        name: 'value',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'Transfer',
  },
  {
    type: 'function',
    inputs: [],
    name: 'BURNER_ROLE',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'CURATOR_ROLE',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'DEFAULT_ADMIN_ROLE',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'DOMAIN_SEPARATOR',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'owner', internalType: 'address', type: 'address' },
      { name: 'spender', internalType: 'address', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'spender', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: 'account', internalType: 'address', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'decimals',
    outputs: [{ name: '', internalType: 'uint8', type: 'uint8' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'depositFor',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: '_account', internalType: 'address', type: 'address' },
      { name: '_value', internalType: 'uint256', type: 'uint256' },
      { name: '_callTarget', internalType: 'address', type: 'address' },
      { name: '_callData', internalType: 'bytes', type: 'bytes' },
    ],
    name: 'depositForAndCall',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'eip712Domain',
    outputs: [
      { name: 'fields', internalType: 'bytes1', type: 'bytes1' },
      { name: 'name', internalType: 'string', type: 'string' },
      { name: 'version', internalType: 'string', type: 'string' },
      { name: 'chainId', internalType: 'uint256', type: 'uint256' },
      { name: 'verifyingContract', internalType: 'address', type: 'address' },
      { name: 'salt', internalType: 'bytes32', type: 'bytes32' },
      { name: 'extensions', internalType: 'uint256[]', type: 'uint256[]' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: '_from', internalType: 'address', type: 'address' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'fractionalReserveBurn',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: '_to', internalType: 'address', type: 'address' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'fractionalReserveMint',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: 'role', internalType: 'bytes32', type: 'bytes32' }],
    name: 'getRoleAdmin',
    outputs: [{ name: '', internalType: 'bytes32', type: 'bytes32' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32' },
      { name: 'account', internalType: 'address', type: 'address' },
    ],
    name: 'grantRole',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32' },
      { name: 'account', internalType: 'address', type: 'address' },
    ],
    name: 'hasRole',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'name',
    outputs: [{ name: '', internalType: 'string', type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'owner', internalType: 'address', type: 'address' }],
    name: 'nonces',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'owner', internalType: 'address', type: 'address' },
      { name: 'spender', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
      { name: 'deadline', internalType: 'uint256', type: 'uint256' },
      { name: 'v', internalType: 'uint8', type: 'uint8' },
      { name: 'r', internalType: 'bytes32', type: 'bytes32' },
      { name: 's', internalType: 'bytes32', type: 'bytes32' },
    ],
    name: 'permit',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32' },
      { name: 'callerConfirmation', internalType: 'address', type: 'address' },
    ],
    name: 'renounceRole',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'requiredReserveRatio',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'pure',
  },
  {
    type: 'function',
    inputs: [
      { name: 'role', internalType: 'bytes32', type: 'bytes32' },
      { name: 'account', internalType: 'address', type: 'address' },
    ],
    name: 'revokeRole',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: '', internalType: 'address', type: 'address' }],
    name: 'segregatedAccount',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'pure',
  },
  {
    type: 'function',
    inputs: [{ name: 'interfaceId', internalType: 'bytes4', type: 'bytes4' }],
    name: 'supportsInterface',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'symbol',
    outputs: [{ name: '', internalType: 'string', type: 'string' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'totalBorrowedSupply',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'totalSegregatedSupply',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'pure',
  },
  {
    type: 'function',
    inputs: [],
    name: 'totalSupply',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'to', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: 'from', internalType: 'address', type: 'address' },
      { name: 'to', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'transferFrom',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'underlying',
    outputs: [{ name: '', internalType: 'contract IERC20', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'withdrawTo',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [
      { name: '_account', internalType: 'address', type: 'address' },
      { name: '_value', internalType: 'uint256', type: 'uint256' },
      { name: '_callTarget', internalType: 'address', type: 'address' },
      { name: '_callData', internalType: 'bytes', type: 'bytes' },
    ],
    name: 'withdrawToAndCall',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'payable',
  },
] as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// React
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useReadErc20 = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"allowance"`
 */
export const useReadErc20Allowance = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'allowance',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"balanceOf"`
 */
export const useReadErc20BalanceOf = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'balanceOf',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"decimals"`
 */
export const useReadErc20Decimals = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'decimals',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"name"`
 */
export const useReadErc20Name = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'name',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"symbol"`
 */
export const useReadErc20Symbol = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'symbol',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"totalSupply"`
 */
export const useReadErc20TotalSupply = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'totalSupply',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useWriteErc20 = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"approve"`
 */
export const useWriteErc20Approve = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'approve',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transfer"`
 */
export const useWriteErc20Transfer = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'transfer',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transferFrom"`
 */
export const useWriteErc20TransferFrom = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'transferFrom',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useSimulateErc20 = /*#__PURE__*/ createUseSimulateContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"approve"`
 */
export const useSimulateErc20Approve = /*#__PURE__*/ createUseSimulateContract({
  abi: erc20Abi,
  functionName: 'approve',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transfer"`
 */
export const useSimulateErc20Transfer = /*#__PURE__*/ createUseSimulateContract(
  { abi: erc20Abi, functionName: 'transfer' },
)

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transferFrom"`
 */
export const useSimulateErc20TransferFrom =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20Abi,
    functionName: 'transferFrom',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__
 */
export const useWatchErc20Event = /*#__PURE__*/ createUseWatchContractEvent({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__ and `eventName` set to `"Approval"`
 */
export const useWatchErc20ApprovalEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20Abi,
    eventName: 'Approval',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__ and `eventName` set to `"Transfer"`
 */
export const useWatchErc20TransferEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20Abi,
    eventName: 'Transfer',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20Vault = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"MIN_MIGRATION_DELAY"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultMinMigrationDelay =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'MIN_MIGRATION_DELAY',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"addressManager"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultAddressManager =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'addressManager',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"bridgedToCanonical"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultBridgedToCanonical =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'bridgedToCanonical',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"btokenDenylist"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultBtokenDenylist =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'btokenDenylist',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"canonicalToBridged"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultCanonicalToBridged =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'canonicalToBridged',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"impl"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultImpl = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'impl',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"inNonReentrant"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultInNonReentrant =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'inNonReentrant',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"lastMigrationStart"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultLastMigrationStart =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'lastMigrationStart',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"lastUnpausedAt"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultLastUnpausedAt =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'lastUnpausedAt',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"name"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultName = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'name',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"owner"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultOwner = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'owner',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"paused"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultPaused = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'paused',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"pendingOwner"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultPendingOwner =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'pendingOwner',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"proxiableUUID"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultProxiableUuid =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'proxiableUUID',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"resolve"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultResolve = /*#__PURE__*/ createUseReadContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'resolve',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"supportsInterface"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useReadErc20VaultSupportsInterface =
  /*#__PURE__*/ createUseReadContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'supportsInterface',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20Vault = /*#__PURE__*/ createUseWriteContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"acceptOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultAcceptOwnership =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'acceptOwnership',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"changeBridgedToken"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultChangeBridgedToken =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'changeBridgedToken',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"init"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultInit = /*#__PURE__*/ createUseWriteContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'init',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"onMessageInvocation"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultOnMessageInvocation =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'onMessageInvocation',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"onMessageRecalled"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultOnMessageRecalled =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'onMessageRecalled',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"pause"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultPause = /*#__PURE__*/ createUseWriteContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'pause',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"renounceOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultRenounceOwnership =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'renounceOwnership',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"sendToken"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultSendToken = /*#__PURE__*/ createUseWriteContract(
  { abi: erc20VaultAbi, address: erc20VaultAddress, functionName: 'sendToken' },
)

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"transferOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultTransferOwnership =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'transferOwnership',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"unpause"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultUnpause = /*#__PURE__*/ createUseWriteContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
  functionName: 'unpause',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"upgradeTo"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultUpgradeTo = /*#__PURE__*/ createUseWriteContract(
  { abi: erc20VaultAbi, address: erc20VaultAddress, functionName: 'upgradeTo' },
)

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"upgradeToAndCall"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWriteErc20VaultUpgradeToAndCall =
  /*#__PURE__*/ createUseWriteContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'upgradeToAndCall',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20Vault = /*#__PURE__*/ createUseSimulateContract({
  abi: erc20VaultAbi,
  address: erc20VaultAddress,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"acceptOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultAcceptOwnership =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'acceptOwnership',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"changeBridgedToken"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultChangeBridgedToken =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'changeBridgedToken',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"init"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultInit =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'init',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"onMessageInvocation"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultOnMessageInvocation =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'onMessageInvocation',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"onMessageRecalled"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultOnMessageRecalled =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'onMessageRecalled',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"pause"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultPause =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'pause',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"renounceOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultRenounceOwnership =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'renounceOwnership',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"sendToken"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultSendToken =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'sendToken',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"transferOwnership"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultTransferOwnership =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'transferOwnership',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"unpause"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultUnpause =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'unpause',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"upgradeTo"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultUpgradeTo =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'upgradeTo',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20VaultAbi}__ and `functionName` set to `"upgradeToAndCall"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useSimulateErc20VaultUpgradeToAndCall =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    functionName: 'upgradeToAndCall',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"AdminChanged"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultAdminChangedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'AdminChanged',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"BeaconUpgraded"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultBeaconUpgradedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'BeaconUpgraded',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"BridgedTokenChanged"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultBridgedTokenChangedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'BridgedTokenChanged',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"BridgedTokenDeployed"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultBridgedTokenDeployedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'BridgedTokenDeployed',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"Initialized"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultInitializedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'Initialized',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"OwnershipTransferStarted"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultOwnershipTransferStartedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'OwnershipTransferStarted',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"OwnershipTransferred"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultOwnershipTransferredEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'OwnershipTransferred',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"Paused"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultPausedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'Paused',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"TokenReceived"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultTokenReceivedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'TokenReceived',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"TokenReleased"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultTokenReleasedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'TokenReleased',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"TokenSent"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultTokenSentEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'TokenSent',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"Unpaused"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultUnpausedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'Unpaused',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20VaultAbi}__ and `eventName` set to `"Upgraded"`
 *
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0x2259662ed5dE0E09943Abe701bc5f5a108eABBAa)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0x1670090000000000000000000000000000000002)
 */
export const useWatchErc20VaultUpgradedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20VaultAbi,
    address: erc20VaultAddress,
    eventName: 'Upgraded',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link ensRegistryAbi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useReadEnsRegistry = /*#__PURE__*/ createUseReadContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"resolver"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useReadEnsRegistryResolver = /*#__PURE__*/ createUseReadContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
  functionName: 'resolver',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"owner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useReadEnsRegistryOwner = /*#__PURE__*/ createUseReadContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
  functionName: 'owner',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"ttl"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useReadEnsRegistryTtl = /*#__PURE__*/ createUseReadContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
  functionName: 'ttl',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link ensRegistryAbi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWriteEnsRegistry = /*#__PURE__*/ createUseWriteContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setSubnodeOwner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWriteEnsRegistrySetSubnodeOwner =
  /*#__PURE__*/ createUseWriteContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setSubnodeOwner',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setTTL"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWriteEnsRegistrySetTtl = /*#__PURE__*/ createUseWriteContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
  functionName: 'setTTL',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setResolver"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWriteEnsRegistrySetResolver =
  /*#__PURE__*/ createUseWriteContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setResolver',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setOwner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWriteEnsRegistrySetOwner = /*#__PURE__*/ createUseWriteContract(
  {
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setOwner',
  },
)

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link ensRegistryAbi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useSimulateEnsRegistry = /*#__PURE__*/ createUseSimulateContract({
  abi: ensRegistryAbi,
  address: ensRegistryAddress,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setSubnodeOwner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useSimulateEnsRegistrySetSubnodeOwner =
  /*#__PURE__*/ createUseSimulateContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setSubnodeOwner',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setTTL"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useSimulateEnsRegistrySetTtl =
  /*#__PURE__*/ createUseSimulateContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setTTL',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setResolver"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useSimulateEnsRegistrySetResolver =
  /*#__PURE__*/ createUseSimulateContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setResolver',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link ensRegistryAbi}__ and `functionName` set to `"setOwner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useSimulateEnsRegistrySetOwner =
  /*#__PURE__*/ createUseSimulateContract({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    functionName: 'setOwner',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link ensRegistryAbi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWatchEnsRegistryEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link ensRegistryAbi}__ and `eventName` set to `"Transfer"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWatchEnsRegistryTransferEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    eventName: 'Transfer',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link ensRegistryAbi}__ and `eventName` set to `"NewOwner"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWatchEnsRegistryNewOwnerEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    eventName: 'NewOwner',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link ensRegistryAbi}__ and `eventName` set to `"NewResolver"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWatchEnsRegistryNewResolverEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    eventName: 'NewResolver',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link ensRegistryAbi}__ and `eventName` set to `"NewTTL"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0x314159265dd8dbb310642f98f50c066173c1259b)
 * - [__View Contract on Sepolia Etherscan__](https://sepolia.etherscan.io/address/0x112234455c3a32fd11230c42e7bccd4a84e02010)
 */
export const useWatchEnsRegistryNewTtlEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: ensRegistryAbi,
    address: ensRegistryAddress,
    eventName: 'NewTTL',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3 = /*#__PURE__*/ createUseReadContract({
  abi: multicall3Abi,
  address: multicall3Address,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getBasefee"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetBasefee = /*#__PURE__*/ createUseReadContract({
  abi: multicall3Abi,
  address: multicall3Address,
  functionName: 'getBasefee',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getBlockHash"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetBlockHash =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getBlockHash',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getBlockNumber"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetBlockNumber =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getBlockNumber',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getChainId"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetChainId = /*#__PURE__*/ createUseReadContract({
  abi: multicall3Abi,
  address: multicall3Address,
  functionName: 'getChainId',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getCurrentBlockCoinbase"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetCurrentBlockCoinbase =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getCurrentBlockCoinbase',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getCurrentBlockDifficulty"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetCurrentBlockDifficulty =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getCurrentBlockDifficulty',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getCurrentBlockGasLimit"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetCurrentBlockGasLimit =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getCurrentBlockGasLimit',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getCurrentBlockTimestamp"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetCurrentBlockTimestamp =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getCurrentBlockTimestamp',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getEthBalance"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetEthBalance =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getEthBalance',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"getLastBlockHash"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useReadMulticall3GetLastBlockHash =
  /*#__PURE__*/ createUseReadContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'getLastBlockHash',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3 = /*#__PURE__*/ createUseWriteContract({
  abi: multicall3Abi,
  address: multicall3Address,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3Aggregate = /*#__PURE__*/ createUseWriteContract(
  { abi: multicall3Abi, address: multicall3Address, functionName: 'aggregate' },
)

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate3"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3Aggregate3 =
  /*#__PURE__*/ createUseWriteContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'aggregate3',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate3Value"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3Aggregate3Value =
  /*#__PURE__*/ createUseWriteContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'aggregate3Value',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"blockAndAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3BlockAndAggregate =
  /*#__PURE__*/ createUseWriteContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'blockAndAggregate',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"tryAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3TryAggregate =
  /*#__PURE__*/ createUseWriteContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'tryAggregate',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"tryBlockAndAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useWriteMulticall3TryBlockAndAggregate =
  /*#__PURE__*/ createUseWriteContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'tryBlockAndAggregate',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3 = /*#__PURE__*/ createUseSimulateContract({
  abi: multicall3Abi,
  address: multicall3Address,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3Aggregate =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'aggregate',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate3"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3Aggregate3 =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'aggregate3',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"aggregate3Value"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3Aggregate3Value =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'aggregate3Value',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"blockAndAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3BlockAndAggregate =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'blockAndAggregate',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"tryAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3TryAggregate =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'tryAggregate',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link multicall3Abi}__ and `functionName` set to `"tryBlockAndAggregate"`
 *
 * - [__View Contract on Ethereum Etherscan__](https://etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Holesky Etherscan__](https://holesky.etherscan.io/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 * - [__View Contract on Taiko Hekla L2 Taikoscan__](https://hekla.taikoscan.network/address/0xcA11bde05977b3631167028862bE2a173976CA11)
 */
export const useSimulateMulticall3TryBlockAndAggregate =
  /*#__PURE__*/ createUseSimulateContract({
    abi: multicall3Abi,
    address: multicall3Address,
    functionName: 'tryBlockAndAggregate',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__
 */
export const useReadRelendWrapped = /*#__PURE__*/ createUseReadContract({
  abi: relendWrappedAbi,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"BURNER_ROLE"`
 */
export const useReadRelendWrappedBurnerRole =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'BURNER_ROLE',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"CURATOR_ROLE"`
 */
export const useReadRelendWrappedCuratorRole =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'CURATOR_ROLE',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"DEFAULT_ADMIN_ROLE"`
 */
export const useReadRelendWrappedDefaultAdminRole =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'DEFAULT_ADMIN_ROLE',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"DOMAIN_SEPARATOR"`
 */
export const useReadRelendWrappedDomainSeparator =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'DOMAIN_SEPARATOR',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"allowance"`
 */
export const useReadRelendWrappedAllowance =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'allowance',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"balanceOf"`
 */
export const useReadRelendWrappedBalanceOf =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'balanceOf',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"decimals"`
 */
export const useReadRelendWrappedDecimals = /*#__PURE__*/ createUseReadContract(
  { abi: relendWrappedAbi, functionName: 'decimals' },
)

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"eip712Domain"`
 */
export const useReadRelendWrappedEip712Domain =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'eip712Domain',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"getRoleAdmin"`
 */
export const useReadRelendWrappedGetRoleAdmin =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'getRoleAdmin',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"hasRole"`
 */
export const useReadRelendWrappedHasRole = /*#__PURE__*/ createUseReadContract({
  abi: relendWrappedAbi,
  functionName: 'hasRole',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"name"`
 */
export const useReadRelendWrappedName = /*#__PURE__*/ createUseReadContract({
  abi: relendWrappedAbi,
  functionName: 'name',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"nonces"`
 */
export const useReadRelendWrappedNonces = /*#__PURE__*/ createUseReadContract({
  abi: relendWrappedAbi,
  functionName: 'nonces',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"requiredReserveRatio"`
 */
export const useReadRelendWrappedRequiredReserveRatio =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'requiredReserveRatio',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"segregatedAccount"`
 */
export const useReadRelendWrappedSegregatedAccount =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'segregatedAccount',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"supportsInterface"`
 */
export const useReadRelendWrappedSupportsInterface =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'supportsInterface',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"symbol"`
 */
export const useReadRelendWrappedSymbol = /*#__PURE__*/ createUseReadContract({
  abi: relendWrappedAbi,
  functionName: 'symbol',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"totalBorrowedSupply"`
 */
export const useReadRelendWrappedTotalBorrowedSupply =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'totalBorrowedSupply',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"totalSegregatedSupply"`
 */
export const useReadRelendWrappedTotalSegregatedSupply =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'totalSegregatedSupply',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"totalSupply"`
 */
export const useReadRelendWrappedTotalSupply =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'totalSupply',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"underlying"`
 */
export const useReadRelendWrappedUnderlying =
  /*#__PURE__*/ createUseReadContract({
    abi: relendWrappedAbi,
    functionName: 'underlying',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__
 */
export const useWriteRelendWrapped = /*#__PURE__*/ createUseWriteContract({
  abi: relendWrappedAbi,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"approve"`
 */
export const useWriteRelendWrappedApprove =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'approve',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"depositFor"`
 */
export const useWriteRelendWrappedDepositFor =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'depositFor',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"depositForAndCall"`
 */
export const useWriteRelendWrappedDepositForAndCall =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'depositForAndCall',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"fractionalReserveBurn"`
 */
export const useWriteRelendWrappedFractionalReserveBurn =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'fractionalReserveBurn',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"fractionalReserveMint"`
 */
export const useWriteRelendWrappedFractionalReserveMint =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'fractionalReserveMint',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"grantRole"`
 */
export const useWriteRelendWrappedGrantRole =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'grantRole',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"permit"`
 */
export const useWriteRelendWrappedPermit = /*#__PURE__*/ createUseWriteContract(
  { abi: relendWrappedAbi, functionName: 'permit' },
)

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"renounceRole"`
 */
export const useWriteRelendWrappedRenounceRole =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'renounceRole',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"revokeRole"`
 */
export const useWriteRelendWrappedRevokeRole =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'revokeRole',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"transfer"`
 */
export const useWriteRelendWrappedTransfer =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'transfer',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"transferFrom"`
 */
export const useWriteRelendWrappedTransferFrom =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'transferFrom',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"withdrawTo"`
 */
export const useWriteRelendWrappedWithdrawTo =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'withdrawTo',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"withdrawToAndCall"`
 */
export const useWriteRelendWrappedWithdrawToAndCall =
  /*#__PURE__*/ createUseWriteContract({
    abi: relendWrappedAbi,
    functionName: 'withdrawToAndCall',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__
 */
export const useSimulateRelendWrapped = /*#__PURE__*/ createUseSimulateContract(
  { abi: relendWrappedAbi },
)

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"approve"`
 */
export const useSimulateRelendWrappedApprove =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'approve',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"depositFor"`
 */
export const useSimulateRelendWrappedDepositFor =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'depositFor',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"depositForAndCall"`
 */
export const useSimulateRelendWrappedDepositForAndCall =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'depositForAndCall',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"fractionalReserveBurn"`
 */
export const useSimulateRelendWrappedFractionalReserveBurn =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'fractionalReserveBurn',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"fractionalReserveMint"`
 */
export const useSimulateRelendWrappedFractionalReserveMint =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'fractionalReserveMint',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"grantRole"`
 */
export const useSimulateRelendWrappedGrantRole =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'grantRole',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"permit"`
 */
export const useSimulateRelendWrappedPermit =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'permit',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"renounceRole"`
 */
export const useSimulateRelendWrappedRenounceRole =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'renounceRole',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"revokeRole"`
 */
export const useSimulateRelendWrappedRevokeRole =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'revokeRole',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"transfer"`
 */
export const useSimulateRelendWrappedTransfer =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'transfer',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"transferFrom"`
 */
export const useSimulateRelendWrappedTransferFrom =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'transferFrom',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"withdrawTo"`
 */
export const useSimulateRelendWrappedWithdrawTo =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'withdrawTo',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link relendWrappedAbi}__ and `functionName` set to `"withdrawToAndCall"`
 */
export const useSimulateRelendWrappedWithdrawToAndCall =
  /*#__PURE__*/ createUseSimulateContract({
    abi: relendWrappedAbi,
    functionName: 'withdrawToAndCall',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__
 */
export const useWatchRelendWrappedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({ abi: relendWrappedAbi })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"Approval"`
 */
export const useWatchRelendWrappedApprovalEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'Approval',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"BurnFractionalReserve"`
 */
export const useWatchRelendWrappedBurnFractionalReserveEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'BurnFractionalReserve',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"EIP712DomainChanged"`
 */
export const useWatchRelendWrappedEip712DomainChangedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'EIP712DomainChanged',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"MintFractionalReserve"`
 */
export const useWatchRelendWrappedMintFractionalReserveEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'MintFractionalReserve',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"RoleAdminChanged"`
 */
export const useWatchRelendWrappedRoleAdminChangedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'RoleAdminChanged',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"RoleGranted"`
 */
export const useWatchRelendWrappedRoleGrantedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'RoleGranted',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"RoleRevoked"`
 */
export const useWatchRelendWrappedRoleRevokedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'RoleRevoked',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"SetSegregatedAccount"`
 */
export const useWatchRelendWrappedSetSegregatedAccountEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'SetSegregatedAccount',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link relendWrappedAbi}__ and `eventName` set to `"Transfer"`
 */
export const useWatchRelendWrappedTransferEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: relendWrappedAbi,
    eventName: 'Transfer',
  })
